import * as THREE from 'three';
import { TAU, lerp, smoothstep, clamp, wrapAngle } from './util.js';
import {
  routeAt, ROUTE_LEN, ROUTE_N, ROUTE_DS, BRIDGE, BRIDGE_SPAN, PLAZA_R, VILLAGE_H,
} from './layout.js';
import { baseHeightAt, heightAt } from './terrain.js';
import { asphaltTexture, dirtTexture, cobbleTexture } from './textures.js';
import { GeoBuilder, propMesh } from './geo.js';

export const ROAD_W = 6.2;
const ROAD_LIFT = 0.07;

// ------------------------------------------------------------------ route heights
function bridgeHump(s) {
  let d = Math.abs(s - BRIDGE.s);
  d = Math.min(d, ROUTE_LEN - d);
  const half = (BRIDGE_SPAN.s1 - BRIDGE_SPAN.s0) / 2 + 3;
  return 0.5 * (1 - smoothstep(0, 1, d / half));
}

const routeYs = new Float32Array(ROUTE_N);
{
  for (let i = 0; i < ROUTE_N; i++) {
    const s = i * ROUTE_DS;
    const p = routeAt(s);
    routeYs[i] = baseHeightAt(p.x, p.z) + ROAD_LIFT + bridgeHump(s);
  }
  // light smoothing so the van glides
  const tmp = new Float32Array(routeYs);
  for (let i = 0; i < ROUTE_N; i++) {
    let sum = 0;
    for (let k = -3; k <= 3; k++) sum += tmp[(i + k + ROUTE_N) % ROUTE_N];
    routeYs[i] = sum / 7;
  }
}

/** Height of the road surface at arc length s. */
export function routeY(s) {
  const f = (((s % ROUTE_LEN) + ROUTE_LEN) % ROUTE_LEN) / ROUTE_DS;
  const i0 = Math.floor(f) % ROUTE_N;
  const i1 = (i0 + 1) % ROUTE_N;
  return lerp(routeYs[i0], routeYs[i1], f - Math.floor(f));
}

// ------------------------------------------------------------------ ribbon builder
/** pts: [{x,y,z}], returns geometry; u across (0..1), v along (metres / vScale). */
function ribbonGeometry(pts, width, vScale, closed = false) {
  const n = pts.length;
  const pos = [], uv = [], idx = [];
  let dist = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[(i - 1 + n) % n], b = pts[(i + 1) % n];
    let tx = b.x - a.x, tz = b.z - a.z;
    if (!closed) {
      if (i === 0) { tx = pts[1].x - pts[0].x; tz = pts[1].z - pts[0].z; }
      if (i === n - 1) { tx = pts[n - 1].x - pts[n - 2].x; tz = pts[n - 1].z - pts[n - 2].z; }
    }
    const l = Math.hypot(tx, tz) || 1;
    tx /= l;
    tz /= l;
    const nx = tz, nz = -tx;
    const p = pts[i];
    const w = (typeof width === 'function' ? width(i / (n - 1)) : width) / 2;
    pos.push(p.x + nx * w, p.y, p.z + nz * w, p.x - nx * w, p.y, p.z - nz * w);
    if (i > 0) dist += Math.hypot(p.x - pts[i - 1].x, p.z - pts[i - 1].z);
    uv.push(0, dist / vScale, 1, dist / vScale);
  }
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const a = i * 2, b = ((i + 1) % n) * 2;
    idx.push(a, a + 1, b, a + 1, b + 1, b);
  }
  if (closed) {
    // final vertex pair duplicates the first with continuing v so texture tiles cleanly
    const last = n * 2;
    pos.push(pos[0], pos[1], pos[2], pos[3], pos[4], pos[5]);
    uv.push(0, (dist + Math.hypot(pts[0].x - pts[n - 1].x, pts[0].z - pts[n - 1].z)) / vScale, 1, (dist + Math.hypot(pts[0].x - pts[n - 1].x, pts[0].z - pts[n - 1].z)) / vScale);
    idx.length -= 6;
    const a = (n - 1) * 2;
    idx.push(a, a + 1, last, a + 1, last + 1, last);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

function roadMaterial(map) {
  return new THREE.MeshStandardMaterial({
    map,
    roughness: 0.95,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
}

// ------------------------------------------------------------------ build
export function createRoads(occ) {
  const group = new THREE.Group();
  group.name = 'roads';

  // ---- main route ribbon
  {
    const step = 1.0;
    const segs = Math.round(ROUTE_LEN / step);
    const vScale = ROUTE_LEN / Math.round(ROUTE_LEN / 6);
    const pts = [];
    for (let i = 0; i < segs; i++) {
      const s = (i / segs) * ROUTE_LEN;
      const p = routeAt(s);
      pts.push({ x: p.x, y: routeY(s), z: p.z });
      occ.disc(p.x, p.z, 3.9, 1);
    }
    const map = asphaltTexture();
    const mesh = new THREE.Mesh(ribbonGeometry(pts, ROAD_W, vScale, true), roadMaterial(map));
    mesh.receiveShadow = true;
    mesh.name = 'route-road';
    group.add(mesh);
  }

  // ---- plaza
  {
    const map = cobbleTexture();
    const geo = new THREE.CircleGeometry(PLAZA_R, 64).rotateX(-Math.PI / 2);
    const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
      map, roughness: 0.9, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3,
    }));
    mesh.position.y = VILLAGE_H + 0.08;
    mesh.receiveShadow = true;
    group.add(mesh);
    // stone curb
    const b = new GeoBuilder();
    const torus = new THREE.TorusGeometry(PLAZA_R + 0.05, 0.28, 5, 72).rotateX(Math.PI / 2);
    b.geometry(torus, '#bdb3a2', { sy: 0.85, jitter: 0.06 });
    const curb = propMesh(b.build());
    curb.position.y = VILLAGE_H + 0.12;
    group.add(curb);
    occ.disc(0, 0, PLAZA_R + 1.4, 1);
  }

  // ---- spokes: dirt roads from the plaza to the ring road
  const spokes = [];
  {
    const angles = [15, 87, 159, 231, 303].map((d) => (d * Math.PI) / 180);
    const map = dirtTexture();
    for (const a of angles) {
      // find the route sample closest to the ray at this angle
      let best = 0, bd = 1e9;
      for (let i = 0; i < ROUTE_N; i += 2) {
        const s = i * ROUTE_DS;
        const p = routeAt(s);
        const da = Math.abs(wrapAngle(Math.atan2(p.z, p.x) - a));
        if (da < bd) { bd = da; best = s; }
      }
      const end = routeAt(best);
      const ex = end.x, ez = end.z;
      const r1 = Math.hypot(ex, ez);
      const nx = Math.cos(a), nz = Math.sin(a);
      const px = -nz, pz = nx;
      const ctrl = [];
      const K = 6;
      for (let k = 0; k <= K; k++) {
        const t = k / K;
        const r = lerp(PLAZA_R - 1.5, r1, t);
        const wob = 2.2 * Math.sin(t * 5 + a * 3) * Math.sin(t * Math.PI);
        ctrl.push(new THREE.Vector3(nx * r + px * wob, 0, nz * r + pz * wob));
      }
      ctrl[K].set(ex, 0, ez);
      const curve = new THREE.CatmullRomCurve3(ctrl, false, 'centripetal');
      const len = curve.getLength();
      const n = Math.ceil(len / 1.6);
      const pts = [];
      for (let i = 0; i <= n; i++) {
        const p = curve.getPointAt(i / n);
        pts.push({ x: p.x, y: baseHeightAt(p.x, p.z) + 0.05, z: p.z });
        occ.disc(p.x, p.z, 2.4, 1);
      }
      const mesh = new THREE.Mesh(ribbonGeometry(pts, 3.4, 4), roadMaterial(map));
      mesh.receiveShadow = true;
      group.add(mesh);
      spokes.push({ angle: a, pts });
    }
  }

  // ---- bridge
  group.add(createBridge());

  return { group, spokes };
}

function createBridge() {
  const b = new GeoBuilder();
  const s0 = BRIDGE_SPAN.s0, s1 = BRIDGE_SPAN.s1;
  const roadW = ROAD_W;
  for (let s = s0; s < s1; s += 1.5) {
    const sm = s + 0.75;
    const p = routeAt(sm);
    const x = p.x, z = p.z, yaw = p.yaw, lx = p.tz, lz = -p.tx;
    const y = routeY(sm);
    b.box(roadW + 1.5, 1.0, 1.75, x, y - 1.05, z, '#b5ada0', { ry: yaw, jitter: 0.06 });
    for (const side of [-1, 1]) {
      const off = (roadW / 2 + 0.5) * side;
      const px = x + lx * off, pz = z + lz * off;
      b.box(0.7, 1.0, 1.78, px, y - 0.05, pz, '#d2cabb', { ry: yaw, jitter: 0.06 });
      b.box(0.98, 0.2, 1.78, px, y + 0.95, pz, '#a39c90', { ry: yaw });
    }
    // abutments down to the banks
    const nearEnd = sm < s0 + 4.5 || sm > s1 - 4.5;
    if (nearEnd) {
      const gy = heightAt(x, z);
      const bottom = gy - 0.4;
      const top = y - 1.05;
      if (top - bottom > 0.05) b.box(roadW + 1.5, top - bottom, 1.75, x, bottom, z, '#9e978b', { ry: yaw, jitter: 0.08 });
    }
  }
  // posts at the four corners
  for (const s of [s0 + 0.2, s1 - 0.2]) {
    const p = routeAt(s);
    const y = routeY(s);
    for (const side of [-1, 1]) {
      const off = (roadW / 2 + 0.5) * side;
      b.box(1.05, 1.5, 1.05, p.x + p.tz * off, y - 0.05, p.z - p.tx * off, '#c9c1b2', { ry: p.yaw });
      b.box(1.25, 0.2, 1.25, p.x + p.tz * off, y + 1.45, p.z - p.tx * off, '#9a9387', { ry: p.yaw });
    }
  }
  const mesh = propMesh(b.build());
  mesh.name = 'bridge';
  return mesh;
}

let _dirtMat = null;
/** Dirt track from a to b ({x,z}) with a gentle meander. Marks the occupancy grid. */
export function createTrack(a, b, width = 3, occ = null) {
  if (!_dirtMat) _dirtMat = roadMaterial(dirtTexture());
  const dx = b.x - a.x, dz = b.z - a.z;
  const len = Math.hypot(dx, dz) || 1;
  const nx = -dz / len, nz = dx / len;
  const ctrl = [new THREE.Vector3(a.x, 0, a.z)];
  const K = Math.max(2, Math.round(len / 14));
  for (let k = 1; k < K; k++) {
    const t = k / K;
    const wob = Math.sin(t * 6 + len) * Math.min(3.5, len * 0.08) * Math.sin(t * Math.PI);
    ctrl.push(new THREE.Vector3(a.x + dx * t + nx * wob, 0, a.z + dz * t + nz * wob));
  }
  ctrl.push(new THREE.Vector3(b.x, 0, b.z));
  const curve = new THREE.CatmullRomCurve3(ctrl, false, 'centripetal');
  const clen = curve.getLength();
  const n = Math.max(2, Math.ceil(clen / 1.6));
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const p = curve.getPointAt(i / n);
    pts.push({ x: p.x, y: baseHeightAt(p.x, p.z) + 0.05, z: p.z });
    if (occ) occ.disc(p.x, p.z, width / 2 + 1.3, 1);
  }
  const mesh = new THREE.Mesh(ribbonGeometry(pts, width, 4), _dirtMat);
  mesh.receiveShadow = true;
  return mesh;
}
